import type { QueryClient } from "@tanstack/react-query";
import {
  FormBuilderQueryKeys,
  getFormBuilderDropdown,
} from "@/api/form-builder/formBuilder.queries";
import type {
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";
import { toFormTypeSlug, toWorkOrderTypeSlug } from "./scopeAssignments";

type DropdownItem = { id: string | number; label?: string };

function itemsOf(payload: unknown): DropdownItem[] {
  const data = (payload as { data?: unknown } | undefined)?.data;
  if (Array.isArray(data)) return data as DropdownItem[];
  const nested = (data as { data?: unknown } | undefined)?.data;
  return Array.isArray(nested) ? (nested as DropdownItem[]) : [];
}

// Same key and request as the New form modal, so both share one cached list.
const fetchDropdown = (queryClient: QueryClient, key: "formType" | "workOrderType") =>
  queryClient.fetchQuery({
    queryKey: [FormBuilderQueryKeys.dropdown, key, undefined],
    queryFn: ({ signal }) =>
      getFormBuilderDropdown("", undefined, signal, key, {
        type: "",
        fields: [],
        limit: 100,
        key,
        filterValue: undefined,
      }),
    staleTime: 5 * 60 * 1000,
  });

/** The editor takes the new form's visit type and category as ids; the assistant speaks in slugs. */
export async function resolveFormTypeIds(
  queryClient: QueryClient,
  workOrderType: VisitCategory,
  formType: FormType,
): Promise<{ formTypeId?: string; workOrderTypeId?: string }> {
  const [formTypes, workOrderTypes] = await Promise.all([
    fetchDropdown(queryClient, "formType"),
    fetchDropdown(queryClient, "workOrderType"),
  ]);
  const formTypeItem = itemsOf(formTypes).find((item) => toFormTypeSlug(item.label) === formType);
  const workOrderTypeItem = itemsOf(workOrderTypes).find(
    (item) => toWorkOrderTypeSlug(item.label) === workOrderType,
  );
  return {
    formTypeId: formTypeItem ? String(formTypeItem.id) : undefined,
    workOrderTypeId: workOrderTypeItem ? String(workOrderTypeItem.id) : undefined,
  };
}
